import { api } from '@sentinel/web-core';
import type {
  MissionReportRow,
  Report,
  ReportCreate,
  ReportTemplate,
  ReportTemplateInput,
  ReportUpdate,
  ReportVerification,
} from '@sentinel/shared';

const base = (missionId: string) => `/missions/${missionId}/reports`;

function withFile(file: Blob, name: string): FormData {
  const form = new FormData();
  form.append('file', file, name);
  return form;
}

/** Report (ADR 0025): drafts, seal, sealed PDFs, verification, templates. */
export const reportEndpoints = {
  overview: () => api<{ items: MissionReportRow[] }>('/reports'),
  verify: (sha256: string) => api<ReportVerification>(`/reports/verify?sha256=${sha256}`),
  list: (missionId: string) => api<{ items: Report[] }>(base(missionId)),
  create: (missionId: string, body: ReportCreate) =>
    api<Report>(base(missionId), { method: 'POST', body }),
  update: (missionId: string, reportId: string, body: ReportUpdate) =>
    api<Report>(`${base(missionId)}/${reportId}`, { method: 'PATCH', body }),
  archive: (missionId: string, reportId: string) =>
    api<undefined>(`${base(missionId)}/${reportId}`, { method: 'DELETE' }),
  uploadMap: (missionId: string, reportId: string, picture: Blob) =>
    api<Report>(`${base(missionId)}/${reportId}/map`, {
      method: 'PUT',
      form: withFile(picture, 'map.png'),
    }),
  preview: (missionId: string, reportId: string) =>
    api<Blob>(`${base(missionId)}/${reportId}/preview`, { as: 'blob' }),
  seal: (missionId: string, reportId: string) =>
    api<Report>(`${base(missionId)}/${reportId}/seal`, { method: 'POST' }),
  pdf: (missionId: string, reportId: string) =>
    api<Blob>(`${base(missionId)}/${reportId}/pdf`, { as: 'blob' }),
  templates: () => api<{ items: ReportTemplate[] }>('/report-templates'),
  createTemplate: (body: ReportTemplateInput) =>
    api<ReportTemplate>('/report-templates', { method: 'POST', body }),
  updateTemplate: (templateId: string, body: ReportTemplateInput) =>
    api<ReportTemplate>(`/report-templates/${templateId}`, { method: 'PUT', body }),
  archiveTemplate: (templateId: string) =>
    api<undefined>(`/report-templates/${templateId}`, { method: 'DELETE' }),
  uploadLogo: (templateId: string, file: File) =>
    api<ReportTemplate>(`/report-templates/${templateId}/logo`, {
      method: 'PUT',
      form: withFile(file, file.name),
    }),
};
